// Documentación oficial — descarga de manuales y reportes en PDF.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FileText, Download, RefreshCw, BookOpen, Loader2, Camera } from "lucide-react";

const COLORS = ["#A43790", "#2C49A0", "#F5B82E", "#3FA84D"];

export default function Documentacion() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/docs")
      .then((r) => setDocs(r.data || []))
      .catch(() => toast.error("No fue posible cargar la documentación"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const download = async (slug, filename) => {
    try {
      const r = await api.get(`/docs/${slug}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Descarga iniciada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo descargar");
    }
  };

  const rebuild = async (slug, refreshScreens = false) => {
    setRebuilding((p) => ({ ...p, [slug]: true }));
    try {
      const r = await api.post(`/docs/${slug}/rebuild`, null, {
        params: refreshScreens ? { refresh_screens: true } : {},
        timeout: 300000,
      });
      if (r.data.accepted) {
        toast.success(r.data.message || "Regeneración iniciada — tomará 2-3 minutos.");
        // Re-check available state cada 30s durante 5min
        let tries = 0;
        const poll = setInterval(() => {
          tries++;
          load();
          if (tries >= 10) clearInterval(poll);
        }, 30000);
      } else {
        toast.success(`Manual regenerado (${r.data.size_kb} KB)`);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo regenerar");
    } finally {
      setRebuilding((p) => ({ ...p, [slug]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="docs-page">
      <div>
        <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">
          Documentación
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] mt-1">
          Manuales y reportes
        </h1>
        <p className="text-sm text-[#6E7591] mt-1 max-w-2xl">
          Documentos oficiales del sistema NEUROMATEMÁTICAS JASQ®. Disponibles
          para descarga en PDF; las versiones se actualizan automáticamente con
          el sistema.
        </p>
      </div>

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]" data-testid="docs-loading">
          Cargando documentos…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d, idx) => {
            const color = COLORS[idx % COLORS.length];
            const isManual = d.slug === "manual-usuario";
            return (
              <div
                key={d.slug}
                data-testid={`doc-card-${d.slug}`}
                className="jasq-card p-5 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${color}22`, color }}
                  >
                    {isManual ? <BookOpen size={20} /> : <FileText size={20} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-extrabold" style={{ color }}>
                      {isManual ? "Guía interactiva" : "Reporte"}
                    </p>
                    <h3 className="font-display text-lg font-extrabold text-[#1F2547] leading-tight truncate">
                      {d.title}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-[#4C5478] flex-1">{d.description}</p>
                <div className="flex items-center gap-2 mt-3 text-[11px] text-[#6E7591]">
                  <span className="font-bold text-[#1F2547]">{d.size_kb} KB</span>
                  <span>·</span>
                  <span>PDF</span>
                  {!d.available && (
                    <span className="text-[#A52258] font-bold ml-auto">No disponible</span>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    disabled={!d.available}
                    onClick={() => download(d.slug, `${d.slug}.pdf`)}
                    data-testid={`download-${d.slug}`}
                    className="jasq-btn-primary inline-flex items-center gap-2 text-sm flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Download size={14} /> Descargar
                  </button>
                  {d.rebuildable && user?.role === "admin" && (
                    <div className="flex gap-1">
                      <button
                        disabled={rebuilding[d.slug]}
                        onClick={() => rebuild(d.slug, false)}
                        data-testid={`rebuild-${d.slug}`}
                        title="Regenera solo el PDF con las capturas existentes (rápido)"
                        className="jasq-btn-ghost inline-flex items-center gap-2 text-sm justify-center disabled:opacity-50"
                      >
                        {rebuilding[d.slug] ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                      </button>
                      <button
                        disabled={rebuilding[d.slug]}
                        onClick={() => rebuild(d.slug, true)}
                        data-testid={`rebuild-full-${d.slug}`}
                        title="Recaptura las pantallas Y regenera el PDF (lento, 2-3 min)"
                        className="jasq-btn-ghost inline-flex items-center gap-2 text-sm justify-center disabled:opacity-50 text-[#A43790]"
                      >
                        {rebuilding[d.slug] ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && docs.length === 0 && (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">
          No hay documentos disponibles para tu rol.
        </div>
      )}
    </div>
  );
}
